"use strict";
var __extends = (this && this.__extends) || function (d, b) {
    for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p];
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
};
var events_1 = require('events');
var vec3 = require('gl-matrix/src/gl-matrix/vec3');
var max = Math.max, min = Math.min, sin = Math.sin, abs = Math.abs, floor = Math.floor, PI = Math.PI;
function clamp(value, to) {
    return isFinite(to) ? max(min(value, to), -to) : value;
}
var v0 = vec3.create();
var v1 = vec3.create();
var m = vec3.create();
var push = vec3.create();
var zeros = vec3.create();
var BaseFpsControl = (function (_super) {
    __extends(BaseFpsControl, _super);
    function BaseFpsControl(state, opts) {
        if (opts === void 0) { opts = {}; }
        _super.call(this);
        this.onfire = function (_) {
        };
        this.state = state;
        this._pitch_target = null;
        this._yaw_target = null;
        this._target = null;
        this.jumping = false;
        this.onfire = opts.onfire || this.onfire;
        this.x_rotation_per_ms = opts.rotationXMax || opts.rotationMax || 33;
        this.y_rotation_per_ms = opts.rotationYMax || opts.rotationMax || 33;
        this.x_rotation_clamp = opts.rotationXClamp || Math.PI / 2;
        this.y_rotation_clamp = opts.rotationYClamp || Infinity;
        this.rotation_scale = opts.rotationScale || 0.002;
        this.state.x_rotation_accum = 0.0;
        this.state.y_rotation_accum = 0.0;
        this.state.z_rotation_accum = 0.0;
        // current state
        this.heading = 0; // radians
        this.running = false;
        this.jumping = false;
        // options:
        this.maxSpeed = 10;
        this.moveForce = 30;
        this.responsiveness = 15;
        this.runningFriction = 0;
        this.standingFriction = 50;
        this.airMoveMult = 0.5;
        this.jumpImpulse = 5;
        this.jumpForce = 6;
        this.jumpTime = 200; // ms
        this.airJumps = 0;
        // internal state
        this._jumpCount = 0;
        this._isJumping = false;
        this._currjumptime = 0;
    }
    BaseFpsControl.prototype.tick = function (dt) {
        if (!this._target)
            return;
        // move implementation originally written as external module
        //   see https://github.com/andyhall/voxel-fps-controller
        //   for original code
        var body = this._target;
        var state = this.state;
        this.jumping = !!state.jump;
        var fb = state.forward ? (state.backward ? 0 : 1) : (state.backward ? -1 : 0);
        var rl = state.left ? (state.right ? 0 : 1) : (state.right ? -1 : 0);
        if ((fb | rl) === 0) {
            this.running = false;
        }
        else {
            this.running = true;
        }
        // jumping
        var onGround = (body.atRestY() < 0);
        var canjump = (onGround || this._jumpCount < this.airJumps);
        if (onGround) {
            this._isJumping = false;
            this._jumpCount = 0;
        }
        // process jump input
        if (this.jumping) {
            if (this._isJumping) {
                if (this._currjumptime > 0) {
                    var jf = this.jumpForce;
                    if (this._currjumptime < dt)
                        jf *= this._currjumptime / dt;
                    body.applyForce(vec3.set(v0, 0, jf, 0));
                    this._currjumptime -= dt;
                }
            }
            else if (canjump) {
                this._isJumping = true;
                if (!onGround)
                    this._jumpCount++;
                this._currjumptime = this.jumpTime;
                body.applyImpulse(vec3.set(v0, 0, this.jumpImpulse, 0));
                // clear downward velocity on airjump
                if (!onGround && body.velocity[1] < 0)
                    body.velocity[1] = 0;
            }
        }
        else {
            this._isJumping = false;
        }
        // apply movement forces if entity is moving, otherwise just friction
        if (this.running) {
            vec3.set(m, 0, 0, 1);
            vec3.transformQuat(m, m, this._yaw_target.quaternion);
            var speed = this.maxSpeed;
            // todo: add crouch/sprint modifiers if needed
            // if (state.sprint) speed *= state.sprintMoveMult
            // if (state.crouch) speed *= state.crouchMoveMult
            vec3.scale(m, m, speed);
            this.heading = 0;
            if (fb) {
                if (fb == -1)
                    this.heading += Math.PI;
                if (rl) {
                    this.heading += Math.PI / 4 * fb * rl; // didn't plan this but it works!
                }
            }
            else {
                this.heading += rl * Math.PI / 2;
            }
            // rotate move vector to entity's heading
            vec3.rotateY(m, m, zeros, this.heading);
            // push vector to achieve desired speed & dir
            // following code to adjust 2D velocity to desired amount is patterned on Quake:
            // https://github.com/id-Software/Quake-III-Arena/blob/master/code/game/bg_pmove.c#L275
            vec3.subtract(push, m, body.velocity);
            push[1] = 0;
            var pushLen = vec3.length(push);
            vec3.normalize(push, push);
            if (pushLen > 0) {
                // pushing force vector
                var canPush = this.moveForce;
                if (!onGround)
                    canPush *= this.airMoveMult;
                // apply final force
                var pushAmt = this.responsiveness * pushLen;
                if (canPush > pushAmt)
                    canPush = pushAmt;
                vec3.scale(push, push, canPush);
                body.applyForce(push);
            }
            // different friction when not moving
            // idea from Sonic: http://info.sonicretro.org/SPG:Running
            body.friction = this.runningFriction;
        }
        else {
            body.friction = this.standingFriction;
        }
        // Rotate
        if (this._yaw_target && this.state.y_rotation_accum) {
            var y_rotation = clamp(this.state.y_rotation_accum * this.rotation_scale, this.y_rotation_per_ms);
            var yaw_target = this._yaw_target;
            yaw_target.rotateY(-clamp(y_rotation, this.y_rotation_per_ms));
        }
        if (this._pitch_target && this.state.x_rotation_accum) {
            var pitch_target = this._pitch_target;
            var x_rotation = clamp(this.state.x_rotation_accum * this.rotation_scale, this.x_rotation_per_ms);
            var finalRotationX = clamp(x_rotation + pitch_target.rotationX, this.x_rotation_clamp);
            pitch_target.setRotationX(finalRotationX);
        }
        this.state.x_rotation_accum = 0;
        this.state.y_rotation_accum = 0;
        this.state.z_rotation_accum = 0;
    };
    BaseFpsControl.prototype.target = function (target, yawTarget, pitchTarget) {
        if (target) {
            this._target = target;
            this._yaw_target = yawTarget;
            this._pitch_target = pitchTarget;
        }
        return this._target;
    };
    return BaseFpsControl;
}(events_1.EventEmitter));
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = BaseFpsControl;
//# sourceMappingURL=BaseFpsControl.js.map


/*****************
 ** WEBPACK FOOTER
 ** /Users/songseunggeun/voxeline/engine/lib/controls/BaseFpsControl.js
 ** module id = 924
 ** module chunks = 0
 **/